/**
 * JobGraph contracts (ADR-004 / E01-F6): multi-step DAGs with per-node
 * retry — the primitive behind UGC, Cinema multi-shot, Explainer and Canvas.
 *
 * P0.5 runtime executes on BullMQ behind a WorkflowEngine port; Temporal
 * slots in at P1 scale without changing these contracts (the "migrate a
 * flow to Temporal only when it becomes a business process" rule).
 *
 * Money semantics: every node carries its own hold — a scene retake
 * charges ONLY that node (spec acceptance E05).
 */
import { z } from 'zod';
export const NODE_KINDS = ['generate', 'tts', 'stitch', 'captions'];
/** Non-generate node inputs (validated per kind at the boundary). */
export const ttsInputSchema = z.object({
    text: z.string().min(1).max(5000),
    voiceId: z.string(),
    locale: z.string().default('en'),
});
export const stitchInputSchema = z.object({
    /** Node keys of video segments, in final order. */
    videoNodes: z.array(z.string()).min(1),
    /** Node keys of narration audio aligned 1:1 with videoNodes (optional). */
    audioNodes: z.array(z.string()).optional(),
    aspect: z.string().default('9:16'),
    /** Fixed per-segment seconds — keeps caption timings sample-accurate. */
    segmentSec: z.number().positive().optional(),
});
export const captionsInputSchema = z.object({
    /** Node key producing the video to burn into. */
    videoNode: z.string(),
    lines: z.array(z.object({ text: z.string(), startSec: z.number(), endSec: z.number() })).min(1),
    locale: z.string().default('en'),
    /** Caption style preset id (data-driven — CaptionStyle packs). */
    styleId: z.string().default('studio-default'),
});
export const graphNodeDefSchema = z.object({
    key: z.string().regex(/^[a-z0-9-]{1,40}$/),
    kind: z.enum(NODE_KINDS),
    /** For kind=generate. */
    spec: z.unknown().optional(),
    /** For tts/stitch/captions. */
    input: z.unknown().optional(),
    dependsOn: z.array(z.string()).default([]),
    /** Human label for progress UI (i18n-neutral, e.g. "Scene 2"). */
    label: z.string().default(''),
});
export const graphDefSchema = z
    .object({ nodes: z.array(graphNodeDefSchema).min(1).max(60) })
    .superRefine((def, ctx) => {
    const keys = new Set(def.nodes.map((n) => n.key));
    if (keys.size !== def.nodes.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'duplicate node keys' });
    }
    for (const n of def.nodes) {
        for (const d of n.dependsOn) {
            if (!keys.has(d))
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: `node ${n.key} depends on unknown ${d}` });
        }
    }
    // Cycle detection (Kahn) — invalid edges are impossible by construction.
    const indeg = new Map();
    const out = new Map();
    for (const n of def.nodes) {
        indeg.set(n.key, n.dependsOn.length);
        for (const d of n.dependsOn)
            out.set(d, [...(out.get(d) ?? []), n.key]);
    }
    const queue = [...indeg.entries()].filter(([, v]) => v === 0).map(([k]) => k);
    let seen = 0;
    while (queue.length) {
        const k = queue.shift();
        seen++;
        for (const next of out.get(k) ?? []) {
            const v = indeg.get(next) - 1;
            indeg.set(next, v);
            if (v === 0)
                queue.push(next);
        }
    }
    if (seen !== def.nodes.length)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'graph contains a cycle' });
});
export const GRAPH_STATES = ['RUNNING', 'SUCCEEDED', 'FAILED', 'CANCELLED'];
//# sourceMappingURL=graphs.js.map